import React, {useEffect, useRef, useState} from "react"
import {graphql, PageProps} from "gatsby"

import {ArrowRight} from "react-feather"
import ScrollIntoView from "react-scroll-into-view"

import Layout from "../components/layout"
import {Button} from "../components/ui"

import ItemPortfolio from "../components/item-portfolio"
import ItemBlog from "../components/item-blog"
import {Form, Description as ContactDescription} from "../components/contact"
import {IndexPageQuery} from "./__generated__/IndexPageQuery"
// @ts-ignore
import LogoDark from "/static/images/svg/logo-black.svg"
// @ts-ignore
import LogoLight from "/static/images/svg/logo-white.svg"
import store from "../utils/store";


export default ({data, location}: PageProps<IndexPageQuery>) => {
    const siteData = data.site.siteMetadata
    const isDarkTheme = data.site.siteMetadata.darkmode;
    const [theme, setTheme] = useState(isDarkTheme ? "1" : "0");

    useEffect(() => {
        if (localStorage.getItem("theme")) {
            setTheme(localStorage.getItem("theme"));
        }
        store.on("theme:change", state => { // TODO: Use tailwind css to set main image this isn't great
            setTheme(localStorage.getItem("theme"));
        });
    }, [])

    const portfolioList = data.portfolio.edges.map((item, _) => (
        <ItemPortfolio
            data={item.node}
            key={`p-item-index-${item.node.id}`}
            even={(_ + 1) % 2 === 0}
        />
    ))

    const blogList = data.blog.edges.map(item => (
        <ItemBlog data={item.node} key={`b-item-index-${item.node.id}`}/>
    ))

    return (
        <Layout
            front={true}
            seo={{
                title: "Home",
                description: siteData.description,
            }}
            navPlaceholder={false}
            location={location}
        >
            <Wall data={siteData} theme={theme}/>
            <Expertise data={siteData}/>
            {siteData.about !== "" && <About data={siteData.about}/>}
            <div className="px-4 lg:px-0" id="portfolio">
                {portfolioList}
            </div>
            <Blog>{blogList}</Blog>
            <Contact data={siteData.contact}/>
        </Layout>
    )
}

const Wall = ({data, theme}) => {
    const wall = useRef(null)

    const twoColumnWall = data.twoColumnWall

    let spanAttrs: Partial<{ style: unknown }> = {}

    if (!twoColumnWall && data.titleImage) {
        spanAttrs.style = {
            backgroundImage: `url('${data.titleImage}')`,
        }

    }

    const innerComponents = (
        <>
            <div className="title bg-bg pb-4">
                <h1
                    className={`text-6xl relative lg:text-7xl ${
                        data.capitalizeTitleOnHome ? "uppercase" : ""
                    }`}
                >
                    <img alt={data.title} src={(theme === "1" || theme === -1) ? LogoLight : LogoDark}/>
                    <span {...spanAttrs}></span>
                </h1>
            </div>
            <h2 className="text-3xl lg:text-4xl text-color-2 pt-4 lg:pt-0">
                {data.introTag}
            </h2>
            <p className="text-base lg:text-lg mt-4">{data.description}</p>
            <Button
                to="/about"
                label="About"
                title="About"
                iconRight={<ArrowRight/>}
            />
        </>
    );

    if (twoColumnWall) {
        return (
            <div
                className="wall h-screen flex relative justify-center items-center overflow-hidden"
                ref={wall}
            >
                <div className="flex-1 lg:block absolute lg:relative w-full h-full top-0 left-0">
                    <div
                        className="absolute left-0 top-0 w-full h-full lg:hidden"
                        style={{
                            background: "rgba(0,0,0,.75)",
                        }}
                    ></div>
                    <img
                        src={data.titleImage}
                        alt=""
                        className="h-full w-auto max-w-none lg:h-auto lg:w-full"
                    />
                </div>
                <div
                    className="flex-1 text-center p-3 relative z-10 lg:text-left lg:pl-8 text-white lg:text-color-default">
                    {innerComponents}
                </div>
            </div>
        )
    }

    return (
        <div
            className="wall h-screen flex flex-col justify-center items-center text-center"
            ref={wall}
        >
            {innerComponents}
        </div>
    )
}

const Expertise = ({data}) => {
    const wall = useRef(null)

    const twoColumnWall = data.twoColumnWall

    const [state, changeState] = useState({
        loaded: false,
        supportsBlend: false,
    })

    useEffect(() => {
        if (window.CSS && !state.loaded) {
            if (CSS.supports("mix-blend-mode", "screen")) {
                wall.current.classList.add("supports-blend")
                changeState({
                    loaded: true,
                    supportsBlend: true,
                })
            }
        }
    }, [state.loaded])

    let spanAttrs: Partial<{ style: unknown }> = {}

    const innerComponents = (
        <div style={{ minWidth: "100vw" }} className="flex justify-center pt-4">
            <div style={{ maxWidth: "1000px" }} className="lg:w-full px-2">
                <div className="post-content">
                    <h2>
                        Our Expertise
                    </h2>
                </div>
                <div className="flex flex-wrap justify-center">
                    <div className="w-full lg:w-1/3">
                        <div className="font-bold">ASP.NET Development</div>
                        <ul className="no-bullet">
                            <li className="mb-2">Web APIs / REST APIs</li>
                            <li className="mb-2">ASP.NET MVC</li>
                            <li className="mb-2">Unit and Integration Testing</li>
                            <li className="mb-2">Security and Practices</li>
                        </ul>
                    </div>
                    <div className="w-full lg:w-1/3">
                        <div className="font-bold">Microsoft Azure</div>
                        <ul className="no-bullet">
                            <li className="mb-2">App Services</li>
                            <li className="mb-2">Functions</li>
                            <li className="mb-2">Azure SQL Database</li>
                            <li className="mb-2">Security and Compliance</li>
                            <li className="mb-2">Storage</li>
                            <li className="mb-2">Kubernetes (AKS)</li>
                        </ul>
                    </div>
                    <div className="w-full lg:w-1/3">
                        <div className="font-bold">React Development</div>
                        <ul className="no-bullet">
                            <li className="mb-2">React</li>
                            <li className="mb-2">Redux</li>
                            <li className="mb-2">TypeScript</li>
                            <li className="mb-2">Next.js</li>
                            <li className="mb-2">Design and UX</li>
                            <li className="mb-2">Unit and Integration Testing</li>
                        </ul>
                    </div>
                </div>
                <ScrollIntoView selector="#portfolio">
                    <Button
                        title="What Our Clients Say"
                        type="button"
                        iconRight={<ArrowRight/>}
                    />
                </ScrollIntoView>
            </div>
        </div>
    )

    return (
        <div
            className="wall flex relative justify-center items-center overflow-hidden"
            ref={wall}
        >
            <div
                className="flex-1 text-center p-3 relative z-10 lg:text-left lg:pl-8 text-white lg:text-color-default">
                {innerComponents}
            </div>
            <div className="flex-1 lg:block absolute lg:relative w-full h-full top-0 left-0">
                <div
                    className="absolute left-0 top-0 w-full h-full lg:hidden"
                    style={{
                        background: "rgba(0,0,0,.75)",
                    }}
                />
                <img
                    src={data.expertiseImage}
                    alt=""
                    className="h-full w-auto max-w-none lg:h-auto lg:w-full rounded"
                />
            </div>
        </div>
    )
}

const About = ({data}) => {
    return (
        <div className="boxed">
            <div className="px-4 py-20 text-center lg:py-40 lg:px-0">
                <h2 className="text-color-1 font-black text-5xl lg:text-6xl">
                    About
                </h2>
                <p className="mt-5 text-lg">{data}</p>
            </div>
        </div>
    )
}

const Blog = ({children}) => {
    return (
        <div className="container mx-auto px-0">
            <div className="pt-20 pb-10 text-center lg:pt-40 lg:pb-20">
                <h2 className="text-color-1 font-black text-5xl lg:text-6xl">
                    Blog
                </h2>
            </div>
            <div className="flex flex-wrap">{children}</div>
        </div>
    )
}

const Contact = ({data}) => {
    const hasContactForm = data.api_url
    return (
        <div className="container mx-auto">
            <div className="pt-20 pb-10 lg:pt-40 lg:pb-20 text-center">
                <h2 className="text-color-1 font-black text-5xl lg:text-6xl">
                    Contact
                </h2>
            </div>
            <div className="flex flex-wrap pb-40">
                {hasContactForm && (
                    <div className="w-full lg:w-1/2 px-4 lg:pl-2 lg:pr-6">
                        <Form api={data.api_url}/>
                    </div>
                )}
                <div
                    className={`w-full ${
                        hasContactForm ? "lg:w-1/2" : "lg:w-2/3 mx-auto"
                    } px-6 pt-8`}
                >
                    <ContactDescription data={data}/>
                </div>
            </div>
        </div>
    )
}

export const query = graphql`
    query IndexPageQuery {
        site: site {
            siteMetadata {
                title
                description
                capitalizeTitleOnHome
                titleImage
                ogImage
                twoColumnWall
                introTag
                description
                about
                contact {
                    api_url
                    description
                    mail
                    phone
                    address
                }
                social {
                    name
                    url
                    icon
                }
                darkmode
            }
        }
        portfolio: allMdx(
            filter: { fields: { sourceName: { eq: "portfolio" } } }
            limit: 6
        ) {
            edges {
                node {
                    id
                    frontmatter {
                        title
                        description
                        image {
                            childImageSharp {
                                fluid(maxWidth: 1000) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    fields {
                        slug
                    }
                }
            }
        }
        blog: allMdx(
            filter: { fields: { sourceName: { eq: "blog" } } }
            limit: 6
            sort: { fields: [frontmatter___date], order: DESC }
        ) {
            edges {
                node {
                    id
                    frontmatter {
                        title
                        description
                        date(formatString: "DD MMMM YYYY")
                        image {
                            childImageSharp {
                                fluid(maxWidth: 1000) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    fields {
                        slug
                    }
                }
            }
        }
    }
`
